import { MenuListItem } from "@atpco/hub-ui";
import { TargetingRule } from "../../uas/editing/targeting/targeting-rule.interface";

export const RulesOperatorMap: Record<string, string> = {
  'not-applicable': '',
  'is-in': 'Is in the list',
  'is-not-in': 'Is not in the list',
  'is-before': 'Is before',
  'is-not-before': 'Is not before',
  'is-after': 'Is after',
  'is': 'Is',
  'is-not': 'Is not',
  'is-between': 'Is between',
  'are-between': 'Are between',
  'is-not-between': 'Is not between',
  'is-marketed-by': 'Marketed by',
  'is-operated-by': 'Operated by',
} as const;

export interface RulesTemplate {
  id: string;
  name: string;
  option_type_ids: string[]
  group: string;
}

export const RulesTemplateMap: Record<string, RulesTemplate> = { 
  'equipment-class-flown': {
    id: 'equipment-class-flown',
    name: 'Equipment category',
    option_type_ids: ['equipment_class', 'equipment_class_list'],
    group: 'Aircraft'
  },
  'arrival-airport': {
    id: 'arrival-airport',
    name: 'Airport',
    option_type_ids: ['airport', 'airport_list'],
    group: 'Arrival'
  },
  'arrival-country': {
    id: 'arrival-country',
    name: 'Country',
    option_type_ids: ['country', 'country_list'],
    group: 'Arrival'
  },
  'arrival-or-departure-airport': {
    id: 'arrival-or-departure-airport',
    name: 'Airport',
    option_type_ids: ['airport', 'airport_list'],
    group: 'Departure or Arrival'
  },
  'arrival-or-departure-country': {
    id: 'arrival-or-departure-country',
    name: 'Country',
    option_type_ids: ['country', 'country_list'],
    group: 'Departure or Arrival'
  },
  'domestic': {
    id: 'domestic',
    name: 'Domestic flight within country',
    option_type_ids: ['country', 'country_list'],
    group: 'Flight'
  },
  'arrival-time': {
    id: 'arrival-time',
    name: 'Time',
    option_type_ids: ['time', 'time_interval'],
    group: 'Arrival'
  },
  'departure-airport': {
    id: 'departure-airport',
    name: 'Airport',
    option_type_ids: ['airport', 'airport_list'],
    group: 'Departure'
  },
  'departure-country': {
    id: 'departure-country',
    name: 'Country',
    option_type_ids: ['country', 'country_list'],
    group: 'Departure'
  },
  'departure-terminal': {
    id: 'departure-terminal',
    name: 'Terminal',
    option_type_ids: ['terminal', 'terminal_list'],
    group: 'Departure'
  },
  'departure-time': {
    id: 'departure-time',
    name: 'Time',
    option_type_ids: ['time', 'time_interval'],
    group: 'Departure'
  },
  'flight-date': {
    id: 'flight-date',
    name: 'Departure date',
    option_type_ids: ['date_interval'],
    group: 'Flight'
  },
  'elapsed-time': {
    id: 'elapsed-time',
    name: 'Duration',
    option_type_ids: ['duration_interval'],
    group: 'Flight'
  },
  'arrival-layover-duration': {
    id: 'arrival-layover-duration',
    name: 'Layover duration',
    option_type_ids: ['duration_interval'],
    group: 'Arrival'
  },
  'amenity-layout': {
    id: 'amenity-layout',
    name: 'Cabin layout',
    option_type_ids: ['layout', 'layout_list'],
    group: 'In-flight amenities'
  },
  'amenity-seat': {
    id: 'amenity-seat',
    name: 'Onboard seat',
    option_type_ids: ['seat', 'seat_list'],
    group: 'In-flight amenities'
  },
  'amenity-entertainment': {
    id: 'amenity-entertainment',
    name: 'Onboard entertainment',
    option_type_ids: ['entertainment', 'entertainment_list'],
    group: 'In-flight amenities'
  },
  'amenity-power': {
    id: 'amenity-power',
    name: 'Onboard power',
    option_type_ids: ['power', 'power_list'],
    group: 'In-flight amenities'
  },
  'amenity-food': {
    id: 'amenity-food',
    name: 'Onboard food',
    option_type_ids: ['food', 'food_list'],
    group: 'In-flight amenities'
  },
  'amenity-beverage': {
    id: 'amenity-beverage',
    name: 'Onboard beverage',
    option_type_ids: ['beverage', 'beverage_list'],
    group: 'In-flight amenities'
  },
  'amenity-wifi': {
    id: 'amenity-wifi',
    name: 'Onboard wifi',
    option_type_ids: ['wifi', 'wifi_list'],
    group: 'In-flight amenities'
  },
  'origin-and-destination': {
    id: 'origin-and-destination',
    name: 'Origin & destination',
    option_type_ids: ['origin_and_destination', 'origin_and_destination_list'],
    group: 'Flight'
  },
  'subfleet-flown': {
    id: 'subfleet-flown',
    name: 'Subfleet',
    option_type_ids: ['subfleet', 'subfleet_list'],
    group: 'Aircraft'
  },
  'codeshare-flight': {
    id: 'codeshare-flight',
    name: 'Flight (codeshare)',
    option_type_ids: ['carriers'],
    group: 'Partnership'
  },
  'not-codeshare': {
    id: 'not-codeshare',
    name: 'Flight (is not a codeshare)',
    option_type_ids: ['not_applicable'],
    group: 'Partnership'
  },
  'display-dates': {
    id: 'display-dates',
    name: 'Dates',
    option_type_ids: ['display_dates'],
    group: 'Display'
  },
  'stopover-connection': {
    id: 'stopover-connection',
    name: 'Stopover/connecting airport',
    option_type_ids: ['stopover_connection', 'stopover_connection_list'],
    group: 'Flight'
  },
};

export const MarketRulesTemplateMap = {
  'arrival-airport': {
    id: 'arrival-airport',
    name: 'Arrival airport',
    option_type_ids: ['airport_list_id', 'airport_code'],
  },
  'arrival-country': {
    id: 'arrival-country',
    name: 'Arrival country',
    option_type_ids: ['country_list_id', 'country_code'],
  },
  'departure-airport': {
    id: 'departure-airport',
    name: 'Departure airport',
    option_type_ids: ['airport_list_id', 'airport_code'],
  },
  'departure-country': {
    id: 'departure-country',
    name: 'Departure country',
    option_type_ids: ['country_list_id', 'country_code'],
  },
  'origin-and-destination': {
    id: 'origin-and-destination',
    name: 'Origin & destination',
    option_type_ids: ['origin_and_destination_list_id', 'from'],
  },
};

const transformAmenity: Function = (rule: TargetingRule, values?: string[]) => {
  if (values) {
    rule.display_text_name = values[1];
    rule.display_text_key = values[0];
    rule.option_id = values[0];
  } else {
    rule.option_id = rule.display_text_key;
  }
}

const amenitiesHelpText: string = 'Options will be shown based on selected cabins';
const amenitiesPlaceholderText: string = 'Select display text';
/**
 * This function is needed to initialize equipment_class_value before sending it to the backend.
 * Also, initializez option_id for it to be displayed on the rule component page.
 * @param rule 
 * @param values 
 */
const transformEquipmentClass: Function = (rule: TargetingRule, values?: string[]) => {
  if(values) {
    rule.equipment_class_value = values[0];
    rule.option_id = values[0];  
  }
  else{
    rule.option_id = rule.equipment_class_value;
  }
}

export const RulesOptionTypeMap: Record<string, { id: string, operator_ids: string[], transform?: Function, helpText?: string, placeHolderText?: string }> = {
  airport_list: {
    id: 'airport_list',
    operator_ids: ['is-in', 'is-not-in'],
  },
  airport: {
    id: 'airport',
    operator_ids: ['is', 'is-not'],
  },
  country_list: {
    id: 'country_list',
    operator_ids: ['is-in', 'is-not-in'],
  },
  country: {
    id: 'country',
    operator_ids: ['is', 'is-not'],
  },
  terminal_list: {
    id: 'terminal_list',
    operator_ids: ['is-in', 'is-not-in'],
  },
  terminal: {
    id: 'terminal',
    operator_ids: ['is', 'is-not'],
  },
  time: {
    id: 'time',
    operator_ids: ['is-before', 'is-after'],
  },
  time_interval: {
    id: 'time_interval',
    operator_ids: ['is-between'],
  },
  duration_interval: {
    id: 'duration_interval',
    operator_ids: ['is-between', 'is-not-between'],
  },
  date_interval: {
    id: 'date_interval',
    operator_ids: ['is-between', 'is-not-between'],
  },
  origin_and_destination_list: {
    id: 'origin_and_destination_list',
    operator_ids: ['is-in', 'is-not-in'],
  },
  origin_and_destination: {
    id: 'origin_and_destination',
    operator_ids: ['is', 'is-not'],
  },
  subfleet_list: {
    id: 'subfleet_list',
    operator_ids: ['is-in', 'is-not-in'],
  },
  subfleet: {
    id: 'subfleet',
    operator_ids: ['is', 'is-not'],
  },
  tail_list: {
    id: 'tail_list',
    operator_ids: ['is-in', 'is-not-in'],
  },
  carriers: {
    id: 'carriers',
    operator_ids: ['is-marketed-by', 'is-operated-by'],
  },
  not_applicable: {
    id: 'not_applicable',
    operator_ids: ['not-applicable'],
  },
  position_list: {
    id: 'position_list',
    operator_ids: ['is', 'is-not'],
  },
  layout: {
    id: 'layout',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  layout_list: {
    id: 'layout_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  seat: {
    id: 'seat',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  seat_list: {
    id: 'seat_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  entertainment: {
    id: 'entertainment',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  entertainment_list: {
    id: 'entertainment_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  power: {
    id: 'power',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  power_list: {
    id: 'power_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  food: {
    id: 'food',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  food_list: {
    id: 'food_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  beverage: {
    id: 'beverage',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  beverage_list: {
    id: 'beverage_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  wifi: {
    id: 'wifi',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  wifi_list: {
    id: 'wifi_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  //Note - This option type is treated as a list even though the operator_ids are 'is' and 'is-not'
   equipment_class: {
     id: 'equipment_class',
     operator_ids: ['is', 'is-not'],
     transform: transformEquipmentClass
   },
  equipment_class_list: {
    id: 'equipment_class_list',
    operator_ids: ['is-in', 'is-not-in'],
  },
  display_dates: {
    id: 'display_dates',
    operator_ids: ['are-between'],
  },
  stopover_connection: {
    id: 'stopover_connection',
    operator_ids: ['is']
  },
  stopover_connection_list: {
    id: 'stopover_connection_list',
    operator_ids: ['is-in']
  },
  stopover_duration_interval: {
    id: 'duration_interval',
    operator_ids: ['layover-is-between', 'layover-is-not-between']
  },
};

export const MarketRulesOptionTypeMap: Record<string, { id: string, operand_key: string, operator_ids: string[] }> = {
  airport_list_id: {
    id: 'airport_list',
    operand_key: 'airport_list_id',
    operator_ids: ['is-in', 'is-not-in'],
  },
  airport_code: {
    id: 'airport',
    operand_key: 'airport_code',
    operator_ids: ['is', 'is-not'],
  },
  country_list_id: {
    id: 'country_list',
    operand_key: 'country_list_id',
    operator_ids: ['is-in', 'is-not-in'],
  },
  country_code: {
    id: 'country',
    operand_key: 'country_code',
    operator_ids: ['is', 'is-not'],
  },
  origin_and_destination_list_id: {
    id: 'origin_and_destination_list',
    operand_key: 'origin_and_destination_list_id',
    operator_ids: ['is-in', 'is-not-in'],
  },
  from: {
    id: 'origin_and_destination',
    operand_key: 'from',
    operator_ids: ['is', 'is-not'],
  },
};

export const SUGGESTED_RULES_MAP: Record<string, MenuListItem[]> = {
  aircraft: [
    {
      name: 'Suggested rules for Aircraft category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Aircraft: Cabin layout',
      id: 'amenity-layout',
      description: '(e.g., 1-2 layout)',
    },
    {
      name: 'Aircraft: Subfleet',
      id: 'subfleet-flown',
      description: '(e.g., A330)',
    },
    {
      name: 'Aircraft: Equipment category',
      id: 'equipment-class-flown',
      description: '(e.g., Narrowbody Aircraft)',
    }
  ],
  seat: [
    {
      name: 'Suggested rules for Seat category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Aircraft: Subfleet',
      id: 'subfleet-flown',
      description: '(e.g., A330)',
    },
    {
      name: 'In-flight amenities: Onboard seat',
      id: 'amenity-seat',
      description: '(e.g., 30” seat pitch)',
    },
  ],
  meals: [
    {
      name: 'Suggested rules for Meals category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Departure or Arrival: Airport',
      id: 'arrival-or-departure-airport',
    },
    {
      name: 'Departure or Arrival: Country',
      id: 'arrival-or-departure-country',
    },
    {
      name: 'Flight: Duration',
      id: 'elapsed-time',
    },
    {
      name: 'In-flight amenities: Onboard food',
      id: 'amenity-food',
      description: '(e.g., Meals provided)',
    }
  ],
  beverages: [
    {
      name: 'Suggested rules for Beverage category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Departure or Arrival: Airport',
      id: 'arrival-or-departure-airport',
    },
    {
      name: 'Departure or Arrival: Country',
      id: 'arrival-or-departure-country',
    },
    {
      name: 'Flight: Duration',
      id: 'elapsed-time',
    },
    {
      name: 'In-flight amenities: Onboard beverages',
      id: 'amenity-beverage',
      description: '(e.g., Alcohol (fee) & beverages provided)',
    }
  ],
  entertainment: [
    {
      name: 'Suggested rules for Entertainment category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'In-flight amenities: Entertainment',
      id: 'amenity-entertainment',
      description: '(e.g., Streaming entertainment)'
    },
    {
      name: 'Aircraft: Subfleet',
      id: 'subfleet-flown',
      description: '(e.g., A330)',
    }
  ],
  power: [
    {
      name: 'Suggested rules for Power category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'In-flight amenities: Onboard power',
      id: 'amenity-power',
      description: '(e.g., Power outlet)',
    }
  ],
  'wi-fi': [
    {
      name: 'Suggested rules for Wifi category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'In-flight amenities: Onboard wifi',
      id: 'amenity-wifi',
      description: '(e.g., Basic web browsing)',
    }
  ],
  'service-inflight': [
    {
      name: 'Suggested rules for Service in-flight category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Aircraft: Equipment category',
      id: 'equipment-class-flown',
      description: '(e.g., Narrowbody Aircraft)',
    },
    {
      name: 'Aircraft: Subfleet',
      id: 'subfleet-flown',
      description: '(e.g., A330)',
    },
    {
      name: 'Arrival: Airport',
      id: 'arrival-airport',
    },
    {
      name: 'Arrival: Country',
      id: 'arrival-country',
    },
    {
      name: 'Departure: Airport',
      id: 'departure-airport',
    },
    {
      name: 'Departure: Country',
      id: 'departure-country',
    },
    {
      name: 'Departure of Arrival: Airport',
      id: 'arrival-or-departure-airport',
    },
    {
      name: 'Departure of Arrival: Country',
      id: 'arrival-or-departure-country',
    },
    {
      name: 'Flight: Duration',
      id: 'elapsed-time',
    }
  ],
  upgrade: [
    {
      name: 'Suggested rules for Upgrade category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Aircraft: Equipment category',
      id: 'equipment-class-flown',
      description: '(e.g., Narrowbody Aircraft)',
    },
    {
      name: 'Aircraft: Subfleet',
      id: 'subfleet-flown',
      description: '(e.g., A330)',
    },
    {
      name: 'Arrival: Airport',
      id: 'elapsed-time',
    },
    {
      name: 'Arrival: Country',
      id: 'arrival-country',
    },
    {
      name: 'Departure: Airport',
      id: 'departure-airport',
    },
    {
      name: 'Departure: Country',
      id: 'departure-country',
    },
    {
      name: 'Departure of Arrival: Airport',
      id: 'arrival-or-departure-airport',
    },
    {
      name: 'Departure of Arrival: Country',
      id: 'arrival-or-departure-country',
    },
    {
      name: 'In-flight amenities: Onboard seat',
      id: 'amenity-seat',
      description: '(e.g., 30” seat pitch)',
    }
  ],
  airport: [
    {
      name: 'Suggested rules for Airport category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Departure: Airport',
      id: 'departure-airport',
    }
  ],
  'arrival-terminal': [
    {
      name: 'Suggested rules for Arrival terminal category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Arrival: Airport',
      id: 'arrival-airport',
    },
  ],
  'departure-terminal': [
    {
      name: 'Suggested rules for Departure terminal category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Departure: Airport',
      id: 'departure-airport',
    },
    {
      name: 'Departure: Terminal',
      id: 'departure-terminal',
    }
  ],
  'departure-lounge': [
    {
      name: 'Suggested rules for Departure lounge category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Departure: Airport',
      id: 'departure-airport',
    },
    {
      name: 'Departure: Terminal',
      id: 'departure-terminal',
    }
  ],
  'arrival-lounge': [
    {
      name: 'Suggested rules for Arrival lounge category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Arrival: Airport',
      id: 'arrival-airport',
    }
  ],
  'arrival-transport': [
    {
      name: 'Suggested rules for Arrival transport category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Arrival: Airport',
      id: 'arrival-airport',
    }
  ],
  'service-arrival': [
    {
      name: 'Suggested rules for Service (arrival) category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Arrival: Airport',
      id: 'arrival-airport',
    },
  ],
  'transport-to-airport': [
    {
      name: 'Suggested rules for Transport to airport category',
      id: 'suggestedRules',
      isTitle: true,
    },
    {
      name: 'Departure: Airport',
      id: 'departure-airport',
    },
  ]
}

export const TARGETING_RULE_NAMES: MenuListItem[] = [
  {
    name: 'Suggested Rules',
    id: 'suggestedRules',
    isTitle: true,
  },
  {
    name: 'In-flight amenities',
    description: 'Targeting by In-flight amenities automatically matches your Visual to ATPCO\'s managed Attributes data, ensuring it is only seen when applicable',
    id: 'inFlightAmenities',
    isTitle: true,
  },
  {
    name: 'Onboard seat',
    id: 'amenity-seat',
    description: '(e.g., 30” seat pitch)',
  },
  {
    name: 'Cabin layout',
    id: 'amenity-layout',
    description: '(e.g., 1-2 layout)',
  },
  {
    name: 'Onboard food',
    id: 'amenity-food',
    description: '(e.g., Meals provided)',
  },
  {
    name: 'Onboard entertainment',
    id: 'amenity-entertainment',
    description: '(e.g., Streaming entertainment)',
  },
  { 
    name: 'Onboard wifi',
    id: 'amenity-wifi',
    description: '(e.g., Basic web browsing)',
  },
  {
    name: 'Onboard beverage',
    id: 'amenity-beverage',
    description: '(e.g., Alcohol (fee) & beverages provided)',
  },
  {
    name: 'Onboard power',
    id: 'amenity-power',
    description: '(e.g., Power outlet)',
  },
  {
    name: 'Aircraft',
    id: 'aircraft',
    isTitle: true,
  },
  {
    name: 'Subfleet',
    id: 'subfleet-flown',
    description: '(e.g., A330)',
  },
  {
    name: 'Equipment category',
    id: 'equipment-class-flown',
    description: '(e.g., Narrowbody Aircraft)',
  },
  {
    name: 'Departure',
    id: 'departure',
    isTitle: true,
  },
  {
    name: 'Airport',
    id: 'departure-airport',
  },
  {
    name: 'Country',
    id: 'departure-country',
  },
  {
    name: 'Terminal',
    id: 'departure-terminal',
  },
  {
    name: 'Time',
    id: 'departure-time',
  },
  { 
    name: 'Partnership',
    id: 'partnership',
    isTitle: true,
  },
  {
    name: 'Flight (codeshare)',
    id: 'codeshare-flight',
  },
  {
    name: 'Flight (is not codeshare)',
    id: 'not-codeshare',
  },
  {
    name: 'Departure or Arrival',
    id: 'departureOrArrival',
    isTitle: true,
  },
  {
    name: 'Airport',
    id: 'arrival-or-departure-airport',
  },
  {
    name: 'Country',
    id: 'arrival-or-departure-country',
  },
  {
    name: 'Arrival',
    id: 'arrival',
    isTitle: true,
  },
  {
    name: 'Airport',
    id: 'arrival-airport',
  },
  {
    name: 'Country',
    id: 'arrival-country',
  },
  {
    name: 'Time',
    id: 'arrival-time',
  },
  {
    name: 'Flight',
    id: 'flight',
    isTitle: true,
  },
  {
    name: 'Departure date',
    id: 'flight-date',
  },
  {
    name: 'Duration',
    id: 'elapsed-time',
  },
  {
    name: 'Origin & destination',
    id: 'origin-and-destination',
  },
  {
    name: 'Domestic flight within country',
    id: 'domestic',
  },
  {
    name: 'Stopover/connecting airport', 
    id: 'stopover-connection',
  },
  {
    name: 'Display',
    id: 'display',
    isTitle: true,
  },
  {
    name: 'Display dates',
    id: 'display-dates', 
  }
]

export const CONFLICTING_RULES_MAP: Record<string, string[]> = {
  'arrival-airport': [
    'departure-airport',
    'arrival-or-departure-airport',
    'origin-and-destination',
    'stopover-connection',
  ],
  'arrival-country': [
    'departure-country',
    'arrival-or-departure-country',
    'domestic',
  ],
  'departure-airport': [
    'arrival-airport',
    'arrival-or-departure-airport',
    'origin-and-destination',
  ],
  'departure-country': [
    'arrival-country',
    'arrival-or-departure-country',
    'domestic',
  ],
  'arrival-or-departure-airport': [
    'arrival-airport',
    'departure-airport',
    'origin-and-destination',
  ],
  'arrival-or-departure-country': [
    'arrival-country',
    'departure-country',
    'domestic',
  ],
  'domestic': [
    'arrival-country',
    'departure-country',
    'arrival-or-departure-country',
  ],
  'codeshare-flight': [
    'not-codeshare',
  ],
  'not-codeshare': [
    'codeshare-flight',
  ],
  'origin-and-destination': [
    'arrival-airport',
    'departure-airport',
    'arrival-or-departure-airport',
  ],
  'stopover-connection': [
    'arrival-airport',
  ]
}
